/**
 * 运行记录（列表）· 判定卡 02（PR3 升级）。
 * 刀 0 从 Loop 运行记录拷贝起步（来源见 components/plan-lab/COPIED_FROM.json），PR3 按判定卡改：
 * 数据接 A-21（I-18 行）；加列 事项数 ／ 重规划次数 ／ 护栏触发 ／ 快照号，加筛选 快照号 ／ 护栏触发；
 * 删「意图」列与筛选（D-28 删 2-1、2-2）；手写表格 → PlanTable；自写翻页 → ui/Pagination ＋ 底部「共 N 条」；
 * 卡头计数移到底部、卡头只留「刷新」「清空」；清空加 Dialog 二次确认；补空态 ／ 失败态；点行开轨迹抽屉（PR5 按层 ＋ 按事项分组）。
 */
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { Pagination } from "@/components/ui/pagination";
import { LabeledSelect } from "@/components/ui/select";
import {
	type PlanRecordRow,
	planErrorMessage,
	useClearPlanRecords,
	usePlanRecords,
} from "@/lib/api/plan-lab";
import { FailState } from "../kit/fail-state";
import {
	ERROR_TYPE_ZH,
	SOURCE_TONE,
	SOURCE_ZH,
	STOP_REASON_TONE,
	STOP_REASON_ZH,
	zh,
} from "../kit/plan-labels";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { SEALED_CONTENT_TEXT } from "../kit/sealed-notice";
import { SnapshotTag } from "../kit/snapshot-tag";
import { PlanTraceDrawer } from "../trace-drawer/plan-trace-drawer";

export const ALL = "__all__";

/** 筛选下拉（模拟场景页也引用这个名字） */
export const Select = LabeledSelect;

export function shortTime(ts: string): string {
	return ts.replace("T", " ").slice(0, 19);
}

export const HIST_PAGE = 25;

const RESULT_OPTIONS = [
	{ value: ALL, label: "全部" },
	{ value: "done", label: "有事项办完" },
	{ value: "cant", label: "有事项办不了" },
	{ value: "wait", label: "有事项等客户" },
	{ value: "void", label: "整轮作废" },
];

function count(r: PlanRecordRow, key: string): number {
	return Number(r.item_counts?.[key] ?? 0);
}

function matchResult(r: PlanRecordRow, f: string): boolean {
	if (f === "done") return count(r, "办完") > 0;
	if (f === "cant") return count(r, "办不了") > 0;
	if (f === "wait") return count(r, "等客户") > 0;
	if (f === "void") return !!r.error_type;
	return true;
}

function options(values: string[], label?: (v: string) => string) {
	return [
		{ value: ALL, label: "全部" },
		...values.map((v) => ({ value: v, label: label?.(v) ?? v })),
	];
}

function uniq(values: (string | null | undefined)[]): string[] {
	return Array.from(new Set(values.filter((v): v is string => !!v))).sort();
}

export function HistoryPanel() {
	const recs = usePlanRecords();
	const clear = useClearPlanRecords();
	const [drawerId, setDrawerId] = useState<string | null>(null);
	const [confirmClear, setConfirmClear] = useState(false);
	const [fMode, setFMode] = useState(ALL);
	const [fCustomer, setFCustomer] = useState(ALL);
	const [fSkill, setFSkill] = useState(ALL);
	const [fDate, setFDate] = useState(ALL);
	const [fResult, setFResult] = useState(ALL);
	const [fSnapshot, setFSnapshot] = useState(ALL);
	const [fGuard, setFGuard] = useState(ALL);
	const [page, setPage] = useState(0);

	const records = useMemo(() => recs.data?.rows ?? [], [recs.data]);
	const modes = useMemo(() => uniq(records.map((r) => r.source)), [records]);
	const customers = useMemo(() => uniq(records.map((r) => r.customer_id)), [records]);
	const skills = useMemo(() => uniq(records.flatMap((r) => r.calls ?? [])), [records]);
	const dates = useMemo(
		() => uniq(records.map((r) => (r.created_at ?? "").slice(0, 10))).reverse(),
		[records],
	);
	const snapshots = useMemo(() => uniq(records.map((r) => r.snapshot_id)), [records]);

	const filtered = useMemo(
		() =>
			records.filter((r) => {
				if (fMode !== ALL && r.source !== fMode) return false;
				if (fCustomer !== ALL && r.customer_id !== fCustomer) return false;
				if (fSkill !== ALL && !(r.calls ?? []).includes(fSkill)) return false;
				if (fDate !== ALL && (r.created_at ?? "").slice(0, 10) !== fDate) return false;
				if (fSnapshot !== ALL && r.snapshot_id !== fSnapshot) return false;
				if (fGuard === "yes" && !(r.guard_hits > 0)) return false;
				if (fGuard === "no" && r.guard_hits > 0) return false;
				return matchResult(r, fResult);
			}),
		[records, fMode, fCustomer, fSkill, fDate, fResult, fSnapshot, fGuard],
	);
	const pageCount = Math.max(1, Math.ceil(filtered.length / HIST_PAGE));
	const curPage = Math.min(page, pageCount - 1);
	const paged = filtered.slice(curPage * HIST_PAGE, curPage * HIST_PAGE + HIST_PAGE);

	function setF(set: (v: string) => void) {
		return (v: string) => {
			set(v);
			setPage(0);
		};
	}
	function resetFilters() {
		for (const set of [
			setFMode,
			setFCustomer,
			setFSkill,
			setFDate,
			setFResult,
			setFSnapshot,
			setFGuard,
		])
			set(ALL);
		setPage(0);
	}

	const columns: PlanColumn<PlanRecordRow>[] = [
		{
			key: "ts",
			title: "时间",
			className: "font-mono text-xs text-text-muted",
			render: (r) => shortTime(r.created_at ?? ""),
		},
		{
			key: "mode",
			title: "模式",
			render: (r) => (
				<Badge tone={SOURCE_TONE[r.source] ?? "muted"}>{zh(SOURCE_ZH, r.source)}</Badge>
			),
		},
		{
			key: "msg",
			title: "消息 ／ 场景",
			className: "max-w-[240px] text-text-primary",
			render: (r) =>
				r.sealed ? (
					<span className="flex min-w-0 items-center gap-1" data-testid="plan-run-sealed">
						{(r.turn_no ?? 1) > 1 ? <Badge tone="info">第 {r.turn_no} 轮</Badge> : null}
						<span className="truncate text-text-muted">
							{r.scenario_id ? `${r.scenario_id} · ` : ""}
							{SEALED_CONTENT_TEXT}
						</span>
					</span>
				) : (
					<span className="flex min-w-0 items-center gap-1" title={r.message}>
						{(r.turn_no ?? 1) > 1 ? <Badge tone="info">第 {r.turn_no} 轮</Badge> : null}
						<span className="truncate">
							{r.scenario_id ? `${r.scenario_id} · ` : ""}
							{r.message}
						</span>
					</span>
				),
		},
		{
			key: "calls",
			title: "调用链",
			render: (r) => <CellTags items={r.calls ?? []} mono />,
		},
		{
			key: "stop",
			title: "判停",
			render: (r) =>
				r.stop_reason ? (
					<Badge tone={STOP_REASON_TONE[r.stop_reason] ?? "muted"}>
						{zh(STOP_REASON_ZH, r.stop_reason)}
					</Badge>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
		{
			key: "result",
			title: "结果",
			render: (r) => <ResultCell r={r} />,
		},
		{
			key: "items",
			title: "事项数",
			align: "right",
			render: (r) => {
				const total = count(r, "total");
				const idle = count(r, "未激活");
				return (
					<span title={idle ? `其中 ${idle} 件未激活` : undefined}>
						{total}
						{idle ? <span className="text-xs text-text-muted">（未激活 {idle}）</span> : null}
					</span>
				);
			},
		},
		{
			key: "guard",
			title: "护栏触发",
			align: "right",
			render: (r) =>
				r.guard_hits > 0 ? <Badge tone="warning">{r.guard_hits}</Badge> : <span>0</span>,
		},
		{
			key: "snapshot",
			title: "快照号",
			render: (r) => <SnapshotTag id={r.snapshot_id} />,
		},
		{
			key: "open",
			title: "详情",
			render: (r) => (
				<Button
					type="button"
					size="sm"
					variant="outline"
					onClick={(e) => {
						e.stopPropagation();
						setDrawerId(r.record_id);
					}}
				>
					详情
				</Button>
			),
		},
	];

	let body: React.ReactNode;
	if (recs.isLoading) body = <LoadingInline />;
	else if (recs.isError)
		body = (
			<FailState
				testId="plan-records-fail"
				title="运行记录没加载出来"
				reason={planErrorMessage(recs.error)}
				onRetry={() => recs.refetch()}
				retrying={recs.isFetching}
			/>
		);
	else if (!records.length)
		body = (
			<EmptyState
				title="还没有记录"
				desc="去人工提问跑一轮，这里按每一轮归一记录。"
				action={
					<Button type="button" variant="outline" size="sm" asChild>
						<a href="/plan-lab#playground">去人工提问</a>
					</Button>
				}
			/>
		);
	else if (!filtered.length)
		body = (
			<EmptyState
				title="当前筛选无命中"
				desc="换个筛选条件，或清掉筛选看全部。"
				action={
					<Button type="button" variant="outline" size="sm" onClick={resetFilters}>
						清掉筛选
					</Button>
				}
			/>
		);
	else
		body = (
			<PlanTable
				columns={columns}
				rows={paged}
				rowKey={(r) => r.record_id}
				onRowClick={(r) => setDrawerId(r.record_id)}
				rowTestId="plan-run-row"
			/>
		);

	return (
		<Card data-testid="plan-block-records">
			<CardContent className="space-y-3 p-6">
				<div className="flex flex-wrap items-center gap-3">
					{records.length ? (
						<>
							<Select
								label="模式"
								value={fMode}
								onChange={setF(setFMode)}
								options={options(modes, (v) => zh(SOURCE_ZH, v))}
							/>
							<Select
								label="客户"
								value={fCustomer}
								onChange={setF(setFCustomer)}
								options={options(customers)}
							/>
							<Select
								label="skill"
								value={fSkill}
								onChange={setF(setFSkill)}
								options={options(skills)}
							/>
							<Select
								label="结果"
								value={fResult}
								onChange={setF(setFResult)}
								options={RESULT_OPTIONS}
							/>
							<Select
								label="护栏触发"
								value={fGuard}
								onChange={setF(setFGuard)}
								options={[
									{ value: ALL, label: "全部" },
									{ value: "yes", label: "有" },
									{ value: "no", label: "无" },
								]}
							/>
							<Select
								label="快照号"
								value={fSnapshot}
								onChange={setF(setFSnapshot)}
								options={options(snapshots)}
							/>
							<Select
								label="日期"
								value={fDate}
								onChange={setF(setFDate)}
								options={options(dates)}
							/>
						</>
					) : null}
					<div className="ml-auto flex gap-2">
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={recs.isFetching}
							onClick={() => recs.refetch()}
						>
							刷新
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={clear.isPending || !records.some((r) => r.source === "ask")}
							title={records.some((r) => r.source === "ask") ? undefined : "没有人工提问的记录可清"}
							onClick={() => setConfirmClear(true)}
						>
							清空人工提问记录
						</Button>
					</div>
				</div>
				{body}
				{!recs.isLoading && !recs.isError && records.length ? (
					<div className="flex items-center justify-between gap-3">
						<span className="text-xs text-text-muted" data-testid="plan-records-total">
							共 {filtered.length} 条
							{recs.data && recs.data.total > records.length
								? `（只取了最近 ${records.length} 条，共 ${recs.data.total} 条）`
								: ""}
						</span>
						<Pagination page={curPage} pageCount={pageCount} onChange={setPage} />
					</div>
				) : null}
			</CardContent>
			<Dialog open={confirmClear} onOpenChange={setConfirmClear}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>清空人工提问的运行记录？</DialogTitle>
						<DialogDescription>
							清空后列表里不再显示这些记录（评测的记录不受影响）；留痕与花费账本照留。此操作不能撤回。
						</DialogDescription>
					</DialogHeader>
					{clear.isError ? (
						<p className="text-xs text-brand-danger">{planErrorMessage(clear.error)}</p>
					) : null}
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => setConfirmClear(false)}
						>
							取消
						</Button>
						<Button
							type="button"
							variant="danger"
							size="sm"
							disabled={clear.isPending}
							onClick={() =>
								clear.mutate(undefined, {
									onSuccess: () => {
										setConfirmClear(false);
										setPage(0);
									},
								})
							}
						>
							{clear.isPending ? "清空中…" : "确认清空"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<PlanTraceDrawer recordId={drawerId} onClose={() => setDrawerId(null)} />
		</Card>
	);
}

function ResultCell({ r }: { r: PlanRecordRow }) {
	if (r.error_type) return <Badge tone="danger">作废 · {zh(ERROR_TYPE_ZH, r.error_type)}</Badge>;
	const parts: string[] = [];
	for (const k of ["办完", "办不了", "等客户", "转人工", "已撤销"]) {
		const n = count(r, k);
		if (n) parts.push(`${k} ${n}`);
	}
	return <CellTags items={parts} max={2} />;
}
